import authFirebase from "../authFirebase";
import {rtdb} from "../firebase";
import { ContentType } from "../interfaces";
// Firebase modular imports
import { ref, set, onDisconnect, onValue, DatabaseReference } from "firebase/database";

// Initialize database
const database = rtdb;
const auth = authFirebase;

let userStatusDatabaseRef: DatabaseReference | null = null;
let channelStatusDatabaseRef: DatabaseReference | null = null;

export const initFirebasePresence = async (
	contentId: string,
	analyticsUserId: string,
	uid: string,
	contentType: ContentType
) => {
	const currentUser = auth.currentUser;
	if (uid && analyticsUserId) {
		// Warn if the browser doesn't support addEventListener or the Page Visibility API
		//updateHidden();

		const provider = currentUser?.isAnonymous
			? currentUser.displayName
				? "anonymous"
				: "unidentified"
			: currentUser?.providerId || "";

		// We'll create a constant which we will write to
		// the Realtime database when this device is offline
		const isOfflineForDatabase = {
			state: "offline",
			isListening: false,
			firebaseId: uid,
			provider,
			action: "Disconnected",
			contentType,
			userAgent: navigator.userAgent,
		};

		if (userStatusDatabaseRef !== null) {
			//If the user already had a connection to other content, remove it
			await set(userStatusDatabaseRef, { ...isOfflineForDatabase, skipQueue: false });
			//Remove the onDisconnect handler
			await onDisconnect(userStatusDatabaseRef).cancel();
		}

		// Create a reference to this user's specific status node.
		// This is where we will store data about being online/offline.
		userStatusDatabaseRef = ref(database, `/analytics/user-presence/${contentId}/${analyticsUserId}`);

		// Create a reference to the special '.info/connected' path in
		// Realtime Database. This path returns `true` when connected
		// and `false` when disconnected.
		const connectedRef = ref(database, ".info/connected");
		onValue(connectedRef, async (snapshot) => {
			// If we're not currently connected, don't do anything.
			if (snapshot.val() === false) {
				return;
			}

			// If we are currently connected, then use the 'onDisconnect()'
			// method to add a set which will only trigger once this
			// client has disconnected by closing the app,
			// losing internet, or any other means.
			await onDisconnect(userStatusDatabaseRef!)
				.set(isOfflineForDatabase)
				.then(async () => {
					// The promise returned from .onDisconnect().set() will
					// resolve as soon as the server acknowledges the onDisconnect()
					// request, NOT once we've actually disconnected:
					// https://firebase.google.com/docs/reference/js/firebase.database.OnDisconnect

					// We can now safely set ourselves as 'online' knowing that the
					// server will mark us as offline once we lose connection.

					await set(userStatusDatabaseRef!, {
						state: "online",
						isListening: true,
						firebaseId: uid,
						provider,
						action: "Connected",
						contentType,
						userAgent: navigator.userAgent,
					});
				});
		});
	}
};

export const initChannelPresence = (channelId: string, analyticsUserId: string, attendeeNumber: string = "") => {
	if (channelId && analyticsUserId) {
		const isOfflineForDatabase = {
			isOnline: false,
			attendeeNumber,
			displayName: auth.currentUser?.displayName || "",
			uid: auth.currentUser?.uid || "",
		};

		if (channelStatusDatabaseRef !== null) {
			//If the user already had a connection to other content, remove it
			set(channelStatusDatabaseRef, { ...isOfflineForDatabase });
			//Remove the onDisconnect handler
			onDisconnect(channelStatusDatabaseRef).cancel();
		}
		
		channelStatusDatabaseRef = ref(database, `/channel-presence/${channelId}/${analyticsUserId}`);

		onValue(channelStatusDatabaseRef, snap => {
			const val = snap.val();
			if (val?.isOnline === false) {
				set(channelStatusDatabaseRef!, {
					isOnline: true,
					attendeeNumber,
					displayName: auth.currentUser?.displayName || "",
					uid: auth.currentUser?.uid || "",
				});
			}
		});
		
		const connectedRef = ref(database, ".info/connected");
		onValue(connectedRef, snapshot => {
			// If we're not currently connected, don't do anything.
			if (snapshot.val() === false) {
				return;
			}
			onDisconnect(channelStatusDatabaseRef!)
				.set(isOfflineForDatabase)
				.then(() => {
					set(channelStatusDatabaseRef!, {
						isOnline: true,
						attendeeNumber,
						displayName: auth.currentUser?.displayName || "",
						uid: auth.currentUser?.uid || "",
					});
				});
		});
	}
};

export const initEventPresence = (eventNumber: string, analyticsUserId: string, attendeeNumber: string = "") => {
	if (eventNumber && analyticsUserId) {
		const isOfflineForDatabase = {
			isOnline: false,
			attendeeNumber,
			displayName: auth.currentUser?.displayName || "",
			uid: auth.currentUser?.uid || "",
		};

		if (channelStatusDatabaseRef !== null) {
			//If the user already had a connection to other content, remove it
			set(channelStatusDatabaseRef, { ...isOfflineForDatabase });
			//Remove the onDisconnect handler
			onDisconnect(channelStatusDatabaseRef).cancel();
		}
		
		channelStatusDatabaseRef = ref(database, `/event-presence/${eventNumber}/${analyticsUserId}`);

		const connectedRef = ref(database, ".info/connected");
		onValue(connectedRef, snapshot => {
			// If we're not currently connected, don't do anything.
			if (snapshot.val() === false) {
				return;
			}
			onDisconnect(channelStatusDatabaseRef!)
				.set(isOfflineForDatabase)
				.then(() => {
					set(channelStatusDatabaseRef!, {
						isOnline: true,
						attendeeNumber,
						displayName: auth.currentUser?.displayName || "",
						uid: auth.currentUser?.uid || "",
					});
				});
		});
	}
};
